import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  useCollectionBinding,
  useSlotGroup,
  useTokens,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { pickHighestContrast, contrastRatio, relativeLuminance } from '../utils/luminance';

interface SpecialOffer {
  title?: string | null;
  description?: string | null;
  disclaimer?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  enabled?: string | null;
  [key: string]: unknown;
}

const specialsCtaDef: SlotGroupDefinition = {
  label: 'Specials CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now →' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function hasValue(v: unknown): v is string {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== '-' && s !== 'null' && s !== 'undefined' && s !== 'false';
}

function isOfferActive(offer: SpecialOffer | null | undefined): boolean {
  if (!offer) return false;

  // NOTE: we intentionally do NOT consult `offer.enabled`. In the data it
  // is a static, always-`false` default, so honoring it would hide every
  // offer. Visibility is driven solely by content + start/end date window.

  if (!hasValue(offer.title) && !hasValue(offer.description)) return false;

  const now = Date.now();

  if (hasValue(offer.start_date)) {
    const start = new Date(offer.start_date!).getTime();
    if (!isNaN(start) && start > now) return false;
  }

  if (hasValue(offer.end_date)) {
    const end = new Date(offer.end_date!).getTime();
    if (!isNaN(end) && end < now) return false;
  }

  return true;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function setBannerHeight(px: number) {
  const rounded = Math.round(px);
  const current = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--offer-banner-height'),
  ) || 0;
  if (rounded === Math.round(current)) return;
  document.documentElement.style.setProperty('--offer-banner-height', `${rounded}px`);
}

// ============================================================================
// Auto-rotating carousel for multiple offers
// ============================================================================

function useOfferRotation(count: number, intervalMs = 6000) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (count <= 1 || paused) return;
    const id = setInterval(() => setActive(i => (i + 1) % count), intervalMs);
    return () => clearInterval(id);
  }, [count, intervalMs, paused]);

  const goTo = useCallback((i: number) => setActive(i), []);
  const pause = useCallback(() => setPaused(true), []);
  const resume = useCallback(() => setPaused(false), []);

  return { active, goTo, pause, resume };
}

// ============================================================================
// Component
// ============================================================================

export function SpecialOfferBanner() {
  const offers = useCollectionBinding<SpecialOffer>('specialofferbanner.items', {
    collection: 'special_offers',
  });
  const specialsCta = useSlotGroup('specialofferbanner.specials_cta', specialsCtaDef);
  // Hidden when Link Text is deleted or an empty string. Defaults still
  // render "Apply Now →" linking to /apply-now.
  const showSpecialsCta = hasValue(specialsCta.label);

  // Pick the more readable text color for the solid primary banner.
  // Choose between `textOnPrimary` and `textOnSecondary` by WCAG contrast —
  // light primaries flip the text to the dark side and dark primaries
  // keep it light. If both candidates fail WCAG AA, synthesize black/white
  // from the surface luminance so the banner stays readable even with stale
  // on-color tokens.
  const tokens = useTokens();
  const bannerTextColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    const surface = primary;
    const onPrimary = tokens?.color?.textOnPrimary;
    const onSecondary = tokens?.color?.textOnSecondary;
    const candidates = [onPrimary, onSecondary].filter(
      (c): c is string => typeof c === 'string' && c.length > 0,
    );
    const picked = candidates.length ? pickHighestContrast(surface, candidates) : null;
    if (picked && contrastRatio(surface, picked) >= 4.5) return picked;
    return relativeLuminance(surface) > 0.55 ? '#1a1a1a' : '#ffffff';
  }, [tokens]);
  const bannerStyle = bannerTextColor
    ? ({ ['--sob-text' as string]: bannerTextColor } as React.CSSProperties)
    : undefined;

  const [dismissed, setDismissed] = useState(false);
  const [scrolledAway, setScrolledAway] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  // Peak intrinsic content height — used to restore --offer-banner-height when
  // the banner scroll-shows again. Header / hero padding read this offset.
  const layoutBannerHeightRef = useRef(0);

  const activeOffers = useMemo(() => {
    const all = offers || [];
    // Only show offers that are currently active (within their start/end
    // date window). If none are active, show nothing — no fallback.
    return all.filter(isOfferActive);
  }, [offers]);

  const hasOffers = activeOffers.length > 0;
  const isVisible = hasOffers && animateIn && !dismissed && !scrolledAway;
  const multi = activeOffers.length > 1;

  const { active: activeIdx, goTo, pause, resume } = useOfferRotation(activeOffers.length);

  // --offer-banner-height is the live top offset for header padding and hero
  // content. It matches the visible banner height and drops to 0 when
  // scroll-hidden so the header can pin to the top.
  useEffect(() => {
    if (!hasOffers || !bannerRef.current) {
      layoutBannerHeightRef.current = 0;
      setBannerHeight(0);
      return;
    }

    if (dismissed) {
      layoutBannerHeightRef.current = 0;
      setBannerHeight(0);
      return;
    }

    const el = bannerRef.current;
    const applyOffset = () => {
      setBannerHeight(scrolledAway ? 0 : layoutBannerHeightRef.current);
    };

    const measure = () => {
      const rectH = Math.round(el.getBoundingClientRect().height);
      const scrollH = Math.round(el.scrollHeight);
      const intrinsic = Math.max(rectH, scrollH);
      if (intrinsic > 0) {
        layoutBannerHeightRef.current = intrinsic;
      }
      applyOffset();
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
      layoutBannerHeightRef.current = 0;
      setBannerHeight(0);
    };
  }, [hasOffers, activeIdx, dismissed, scrolledAway]);

  // Animate in
  useEffect(() => {
    if (!hasOffers) { setBannerHeight(0); return; }
    const raf = requestAnimationFrame(() => setAnimateIn(true));
    return () => cancelAnimationFrame(raf);
  }, [hasOffers]);

  // Scroll-based auto-hide with hysteresis to prevent bouncing
  useEffect(() => {
    if (!hasOffers || dismissed) return;
    let ticking = false;
    const HIDE_AT = 200;
    const SHOW_AT = 50;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolledAway(prev => {
          let next = prev;
          if (!prev && y > HIDE_AT) next = true;
          if (prev && y < SHOW_AT) next = false;
          if (next !== prev) {
            setBannerHeight(next ? 0 : layoutBannerHeightRef.current);
          }
          return next;
        });
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasOffers, dismissed]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);

  const handleDismiss = useCallback(() => setDismissed(true), []);
  const openModal = useCallback(() => {
    setModalIdx(0);
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  if (!hasOffers) return null;

  const offer = activeOffers[activeIdx] || activeOffers[0];
  const showReopenButton = dismissed || scrolledAway;
  const title = hasValue(offer.title) ? offer.title : '';
  const description = hasValue(offer.description) ? stripHtml(offer.description) : '';
  const rawDisclaimer = offer.disclaimer ?? '';
  const disclaimer = hasValue(rawDisclaimer) ? stripHtml(String(rawDisclaimer)) : '';

  return (
    <>
      <div
        ref={bannerRef}
        className="sob"
        data-visible={isVisible}
        role="banner"
        aria-label="Special offers"
        onMouseEnter={pause}
        onMouseLeave={resume}
        style={bannerStyle}
      >
        {/* Shimmer accent */}
        <div className="sob-shimmer" />

        <div className="sob-inner">
          {/* Offer content — crossfade between offers */}
          <div className="sob-content">
            <EditableCollectionBinding
              bindingPath="specialofferbanner.items"
              label="Special Offers"
            >
            <div className="sob-text" key={activeIdx}>
              {title && (
                <p className="sob-title font-heading uppercase">{title}</p>
              )}
              {description && (
                <p className="sob-desc">{description}</p>
              )}
              {showSpecialsCta && (
                <EditableSlotGroup
                  groupPath="specialofferbanner.specials_cta"
                  definition={specialsCtaDef}
                >
                  <a
                    href={specialsCta.url || '/apply-now'}
                    target={specialsCta._target || '_self'}
                    rel={(specialsCta._target || '_self') === '_blank' ? 'noopener noreferrer' : undefined}
                    className="sob-cta"
                    data-vibesite-event="apply_now_clicked"
                  >
                    {specialsCta.label}
                  </a>
                </EditableSlotGroup>
              )}
              {disclaimer && (
                <p className="sob-disclaimer">{disclaimer}</p>
              )}
            </div>
            </EditableCollectionBinding>

            {/* Dot indicators — centered below content */}
            {multi && (
              <div className="sob-dots" role="tablist" aria-label="Offers">
                {activeOffers.map((_, i) => (
                  <button
                    key={i}
                    className={`sob-dot${i === activeIdx ? ' sob-dot--active' : ''}`}
                    onClick={() => goTo(i)}
                    role="tab"
                    aria-selected={i === activeIdx}
                    aria-label={`Offer ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Close — positioned relative to the full-width .sob container
            (not the capped-width .sob-inner) so its right edge aligns with
            the header's CTA right edge at all viewport widths. Offsets
            match .header-bar-inner / .header-top-strip-inner padding
            (1.5rem / 3.125rem / 4rem). */}
        <button onClick={handleDismiss} className="sob-close" aria-label="Dismiss">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Auto-rotation progress bar */}
        {multi && isVisible && (
          <div className="sob-progress">
            <div className="sob-progress-bar" key={activeIdx} />
          </div>
        )}
      </div>

      {/* Floating pill — opens modal */}
      <button
        onClick={openModal}
        className="sob-reopen"
        style={{
          ...(bannerStyle || {}),
          opacity: showReopenButton ? 1 : 0,
          pointerEvents: showReopenButton ? 'auto' : 'none',
          transform: showReopenButton
            ? 'translateX(-50%) translateY(0)'
            : 'translateX(-50%) translateY(16px)',
        }}
        aria-label="Show special offers"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <span>{activeOffers.length > 1 ? `${activeOffers.length} Special Offers` : 'Special Offer'}</span>
      </button>

      {/* Modal overlay */}
      {modalOpen && (
        <div className="sob-modal-overlay" onClick={closeModal}>
          <div className="sob-modal" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeModal} className="sob-modal-close" aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {(() => {
              const modalOffer = activeOffers[modalIdx] || activeOffers[0];
              const mTitle = hasValue(modalOffer.title) ? modalOffer.title : '';
              const mDesc = hasValue(modalOffer.description) ? stripHtml(modalOffer.description) : '';
              const mRawDisclaimer = modalOffer.disclaimer ?? '';
              const mDisclaimer = hasValue(mRawDisclaimer) ? stripHtml(String(mRawDisclaimer)) : '';

              return (
                <div className="sob-modal-body" key={modalIdx}>
                  {mTitle && <h3 className="sob-modal-title">{mTitle}</h3>}
                  {mDesc && <p className="sob-modal-desc">{mDesc}</p>}
                  {mDisclaimer && <p className="sob-modal-disclaimer">{mDisclaimer}</p>}
                </div>
              );
            })()}

            {multi && (
              <div className="sob-modal-nav">
                <button
                  onClick={() => setModalIdx((modalIdx - 1 + activeOffers.length) % activeOffers.length)}
                  className="sob-modal-arrow"
                  aria-label="Previous offer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <div className="sob-modal-dots">
                  {activeOffers.map((_, i) => (
                    <button
                      key={i}
                      className={`sob-modal-dot${i === modalIdx ? ' sob-modal-dot--active' : ''}`}
                      onClick={() => setModalIdx(i)}
                      aria-label={`Offer ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setModalIdx((modalIdx + 1) % activeOffers.length)}
                  className="sob-modal-arrow"
                  aria-label="Next offer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        /* ================================================================ */
        /* Banner shell                                                     */
        /* ================================================================ */
        .sob {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 60;
          background: var(--vibe-color-primary);
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.4s ease;
        }
        .sob[data-visible="true"] {
          max-height: 300px;
          opacity: 1;
        }

        /* Subtle shimmer sweep across the banner */
        .sob-shimmer {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            110deg,
            transparent 20%,
            rgba(255,255,255,0.06) 40%,
            rgba(255,255,255,0.06) 60%,
            transparent 80%
          );
          background-size: 200% 100%;
          animation: sob-shimmer 6s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes sob-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* ================================================================ */
        /* Inner layout                                                     */
        /* ================================================================ */
        .sob-inner {
          position: relative;
          max-width: 80rem;
          margin: 0 auto;
          /* Right padding clears the close button on the outer .sob. */
          padding: 0.65rem 3.5rem 0.65rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        @media (min-width: 768px) {
          .sob-inner { padding-right: 5.5rem; }
        }
        @media (min-width: 1280px) {
          .sob-inner { padding-right: 6.5rem; }
        }

        /* ================================================================ */
        /* Text content                                                     */
        /* ================================================================ */
        .sob-content {
          flex: 1;
          min-width: 0;
          text-align: center;
        }

        .sob-text {
          animation: sob-fade-in 0.4s ease both;
        }
        @keyframes sob-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .sob-title {
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--sob-text, var(--vibe-color-text-on-primary));
          letter-spacing: 0.02em;
          margin: 0;
          line-height: 1.3;
        }

        .sob-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 80%, transparent);
          margin: 0.15rem 0 0;
          line-height: 1.4;
        }

        .sob-cta {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 700;
          color: var(--sob-text, var(--vibe-color-text-on-primary));
          margin: 0.15rem 0 0;
          line-height: 1.3;
          text-decoration: none;
        }
        .sob-cta:hover {
          text-decoration: underline;
        }

        .sob-disclaimer {
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          font-style: italic;
          color: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 50%, transparent);
          margin: 0.15rem 0 0;
          line-height: 1.3;
        }

        /* ================================================================ */
        /* Dot indicators                                                   */
        /* ================================================================ */
        .sob-dots {
          display: flex;
          justify-content: center;
          gap: 6px;
          margin-top: 0.35rem;
        }
        .sob-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          border: 1.5px solid color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 50%, transparent);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .sob-dot:hover {
          border-color: var(--sob-text, var(--vibe-color-text-on-primary));
          transform: scale(1.2);
        }
        .sob-dot--active {
          background: var(--sob-text, var(--vibe-color-text-on-primary));
          border-color: var(--sob-text, var(--vibe-color-text-on-primary));
        }

        /* ================================================================ */
        /* Close button                                                     */
        /* ================================================================ */
        .sob-close {
          position: absolute;
          /* Right offset matches .header-bar-inner / .header-top-strip-inner
             padding so the X's right edge sits flush with the header CTA. */
          right: 1.5rem;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 26px; height: 26px;
          border-radius: 50%;
          border: 1.5px solid color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 25%, transparent);
          background: transparent;
          color: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 70%, transparent);
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.2s;
        }
        @media (max-width: 767px) {
          .sob-close { right: 1rem; }
        }
        @media (min-width: 768px) {
          .sob-close { right: 3.125rem; }
        }
        @media (min-width: 1280px) {
          .sob-close { right: 4rem; }
        }
        .sob-close:hover {
          background: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 15%, transparent);
          color: var(--sob-text, var(--vibe-color-text-on-primary));
          border-color: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 50%, transparent);
          transform: translateY(-50%) scale(1.1);
        }

        /* ================================================================ */
        /* Auto-rotation progress bar                                       */
        /* ================================================================ */
        .sob-progress {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 2px;
          background: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 10%, transparent);
        }
        .sob-progress-bar {
          height: 100%;
          background: color-mix(in srgb, var(--sob-text, var(--vibe-color-text-on-primary)) 40%, transparent);
          animation: sob-progress-fill 6s linear forwards;
        }
        @keyframes sob-progress-fill {
          from { width: 0; }
          to   { width: 100%; }
        }

        /* ================================================================ */
        /* Floating reopen pill                                             */
        /* ================================================================ */
        .sob-reopen {
          position: fixed;
          bottom: 1.25rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 90;
          display: flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.6rem 1.25rem;
          border-radius: 9999px;
          border: none;
          background: var(--vibe-color-primary);
          color: var(--sob-text, var(--vibe-color-text-on-primary));
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 600;
          letter-spacing: 0.025em;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(0,0,0,0.25);
          transition: opacity 0.35s ease, transform 0.35s ease, box-shadow 0.2s;
        }
        .sob-reopen:hover {
          box-shadow: 0 6px 24px rgba(0,0,0,0.35);
          transform: translateX(-50%) translateY(-3px) !important;
        }

        /* ================================================================ */
        /* Responsive                                                       */
        /* ================================================================ */
        @media (max-width: 640px) {
          .sob-inner { padding: 0.6rem 3rem 0.6rem 0.75rem; gap: 0.5rem; }
          .sob-title { font-size: 0.95rem; }
          .sob-desc { font-size: 0.875rem; }
          .sob-cta { font-size: 0.8rem; }
          .sob-dots { gap: 5px; }
          .sob-dot { width: 6px; height: 6px; }
        }

        /* ================================================================ */
        /* Modal                                                            */
        /* ================================================================ */
        .sob-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 100;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0);
          animation: sob-overlay-in 0.3s ease forwards;
        }
        @keyframes sob-overlay-in {
          to { background: rgba(0, 0, 0, 0.5); }
        }

        .sob-modal {
          position: relative;
          width: 90%;
          max-width: 480px;
          background: white;
          border-radius: 6px;
          padding: 2rem 2rem 1.5rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          opacity: 0;
          transform: translateY(20px) scale(0.97);
          animation: sob-modal-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) 0.05s forwards;
        }
        @keyframes sob-modal-in {
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .sob-modal-close {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .sob-modal-close:hover {
          background: var(--vibe-color-surface);
          color: var(--vibe-color-text);
        }

        .sob-modal-body {
          text-align: center;
          animation: sob-fade-in 0.3s ease both;
        }

        .sob-modal-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.35rem;
          font-weight: 700;
          color: var(--vibe-color-text);
          margin: 0 0 0.75rem;
          line-height: 1.3;
        }

        .sob-modal-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          margin: 0 0 0.75rem;
          line-height: 1.6;
        }

        .sob-modal-disclaimer {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-style: italic;
          color: var(--vibe-color-text-muted);
          opacity: 0.7;
          margin: 0;
          line-height: 1.4;
        }

        .sob-modal-nav {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin-top: 1.25rem;
          padding-top: 1rem;
          border-top: 1px solid var(--vibe-color-border);
        }

        .sob-modal-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: border-color 0.2s, color 0.2s;
        }
        .sob-modal-arrow:hover {
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary);
        }

        .sob-modal-dots {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .sob-modal-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 1.5px solid var(--vibe-color-border);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .sob-modal-dot:hover {
          border-color: var(--vibe-color-primary);
          transform: scale(1.2);
        }
        .sob-modal-dot--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }

        @media (prefers-reduced-motion: reduce) {
          .sob, .sob-reopen, .sob-text { transition: none; animation: none; }
          .sob-shimmer { animation: none; }
          .sob-progress-bar { animation: none; width: 100%; }
          .sob-modal-overlay, .sob-modal, .sob-modal-body { animation: none; opacity: 1; transform: none; background: rgba(0,0,0,0.5); }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SpecialOfferBanner',
  displayName: 'Special Offer Banner',
  tokens: {},
  slotGroups: {
    'specialofferbanner.specials_cta': specialsCtaDef,
  },
  collectionBindings: {
    'specialofferbanner.items': {
      label: 'Special Offers',
      collection: 'special_offers',
    },
  },
};

(SpecialOfferBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SpecialOfferBanner;
